import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_tables.scss'
import * as selectStyles from '@procore/core-css/src/components/_select.scss'

import {
  SizeNS,
  Props,
  TableAttributes,
  DivAttributes,
  TheadAttributes,
  TrAttributes,
  ThAttributes,
  TbodyAttributes,
  InputAttributes,
  SpanAttributes,
  TdAttributes,
  AnchorAttributes,
  TextAreaAttributes,
} from '../utils/types'
import addSubcomponents from '../utils/addSubcomponents'
import Card, { CardLevelNS } from './Card'
import BaseCheckbox, { CheckboxProps } from './Checkbox'
import Icon from './Icon'
import LinkElement from './Link'
import Spinner from './Spinner'
import BaseTextArea, { TextAreaResizeNS } from './TextArea'
import { withDeprecation } from '../effects/Deprecation'
import Select, { SelectProps } from './Select'

// TODO: snugfit should be a prop instead of a variant
export namespace TableCellVariantNS {
  export const asc = 'asc'
  export const desc = 'desc'
  export const none = ''
  export const snugfit = 'snugfit'
}

type InputCellSize =
  | typeof SizeNS.block
  | typeof SizeNS.lg
  | typeof SizeNS.md
  | typeof SizeNS.sm
  | typeof SizeNS.xl
  | typeof SizeNS.xs

type TableCellVariant =
  | typeof TableCellVariantNS.asc
  | typeof TableCellVariantNS.desc
  | typeof TableCellVariantNS.none
  | typeof TableCellVariantNS.snugfit

export namespace TableVariantNS {
  export const fixedLayout = 'fixed'
  export const none = ''
}

type TableVariant =
  | typeof TableVariantNS.fixedLayout
  | typeof TableVariantNS.none

export interface BodyCellProps extends Props {
  snugfit?: boolean
  variant?: TableCellVariant
}

export interface BodyProps extends Props {}

export interface BodyRowProps extends Props {
  overdue?: boolean
}

export interface CaratProps {
  expanded?: boolean
  onClick: React.EventHandler<any>
}

export interface ContainerProps {
  children?: React.ReactNode
  shelf?: boolean
}

export interface CurrencyCellProps extends Props {}

export interface GroupProps extends Props {
  colSpan?: number
  depth?: number
}

export interface GroupTitleProps extends Props {}

export interface HeaderCellProps extends Props {
  sortable?: boolean
  snugfit?: boolean
  variant?: TableCellVariant
}

export interface HeaderProps extends Props {}

export interface HeaderRowProps extends Props {}

export interface IconCellProps extends Props {}

export interface InputCellProps extends Props {
  errorMessage?: string
  loading?: boolean
  size?: InputCellSize
}

export interface LinkCellProps extends Props {
  href: string
}

export interface PercentCellProps extends Props {}

export interface SelectCellProps extends SelectProps {
  errorMessage?: string
  processing?: boolean
}

export interface TableProps extends Props {
  inline?: boolean
  variant?: TableVariant
}

export interface TextAreaCellProps extends Props {
  errorMessage?: string
  loading?: boolean
  resizable?: boolean
}

export interface TextCellProps extends Props {}

const cx = bind(styles)

export const Table = React.forwardRef<
  HTMLTableElement,
  TableAttributes & TableProps
>(({ className, children, inline, variant, ...props }, ref) => (
  <table
    ref={ref}
    {...props}
    className={cx(
      'core-table__table',
      {
        'core-table__table--inline': inline,
        'core-table__table--fixed-layout':
          variant === TableVariantNS.fixedLayout,
      },
      className
    )}
  >
    {children}
  </table>
))

export const Carat = React.forwardRef<
  HTMLDivElement,
  DivAttributes & CaratProps
>(({ expanded = false, onClick = () => {}, ...props }, ref) => (
  <div ref={ref} className={cx('core-table__carat')}>
    <Icon
      icon="chevron-right"
      className={cx({ 'core-table__carat--expanded': expanded })}
      onClick={arg => {}}
      {...props}
    />
  </div>
))

export const Container = React.forwardRef<
  HTMLDivElement,
  DivAttributes & ContainerProps
>(({ children, shelf = false, ...props }, ref) => (
  <Card ref={ref} level={CardLevelNS.thirty} {...props}>
    <div className={cx('core-table', { 'core-table--with-shelf': shelf })}>
      {children}
    </div>
  </Card>
))

export const Header = React.forwardRef<
  HTMLTableSectionElement,
  TheadAttributes & HeaderProps
>(({ className, children, ...props }, ref) => (
  <thead ref={ref} className={cx('core-table__head', className)} {...props}>
    {children}
  </thead>
))

export const HeaderRow = React.forwardRef<
  HTMLTableRowElement,
  TrAttributes & HeaderRowProps
>(({ children, className, ...props }, ref) => (
  <tr ref={ref} className={cx('core-table__head-row', className)} {...props}>
    {children}
  </tr>
))

export const HeaderCell = React.forwardRef<
  HTMLTableHeaderCellElement,
  ThAttributes & HeaderCellProps
>(
  (
    {
      sortable = false,
      className,
      children,
      snugfit = false,
      variant = '',
      ...props
    },
    ref
  ) => (
    <th
      ref={ref}
      {...props}
      className={cx(
        'core-table__th',
        { 'core-table__th--sortable': sortable },
        { 'core-table__th--sortable-asc': variant === TableCellVariantNS.asc },
        {
          'core-table__th--sortable-desc': variant === TableCellVariantNS.desc,
        },
        {
          'core-table__th--snugfit':
            snugfit || variant === TableCellVariantNS.snugfit,
        },
        className
      )}
    >
      <div className={cx('core-table__th__label')}>{children}</div>
    </th>
  )
)

export const Body = React.forwardRef<
  HTMLTableSectionElement,
  TbodyAttributes & BodyProps
>(({ className, children, ...props }, ref) => (
  <tbody ref={ref} className={cx('core-table__body', className)} {...props}>
    {children}
  </tbody>
))

export const Checkbox = React.forwardRef<
  HTMLInputElement,
  InputAttributes & CheckboxProps
>(({ className, ...props }, ref) => (
  <BaseCheckbox
    ref={ref}
    className={cx('core-table__checkbox', className)}
    {...props}
  />
))

export const GroupTitle = React.forwardRef<
  HTMLSpanElement,
  SpanAttributes & GroupTitleProps
>(({ className, children, ...props }, ref) => (
  <span
    ref={ref}
    className={cx('core-table__group-row__title', className)}
    {...props}
  >
    {children}
  </span>
))

export const Group = React.forwardRef<
  HTMLTableRowElement,
  TrAttributes & GroupProps
>(({ className, children, colSpan = 1000, depth = false, ...props }, ref) => (
  <tr
    ref={ref}
    className={cx(
      'core-table__group-row',
      cx({ [`core-table__group-depth-${depth}`]: typeof depth === 'number' }),
      className
    )}
    {...props}
  >
    <td colSpan={colSpan}>
      <div className={cx('core-table__group-row__content')}>
        <div className={cx('core-table__group-row__header')}>{children}</div>
      </div>
    </td>
  </tr>
))

export const BodyRow = React.forwardRef<
  HTMLTableRowElement,
  TrAttributes & BodyRowProps
>(({ className, children, overdue = false, ...props }, ref) => (
  <tr
    ref={ref}
    className={cx(
      'core-table__body-row',
      { ['core-table__body-row--overdue']: overdue },
      className
    )}
    {...props}
  >
    {children}
  </tr>
))

export const BodyCell = React.forwardRef<
  HTMLTableDataCellElement,
  TdAttributes & BodyCellProps
>(
  (
    {
      className,
      children,
      snugfit = false,
      variant = TableCellVariantNS.none,
      ...props
    },
    ref
  ) => (
    <td
      ref={ref}
      className={cx(
        'core-table__td',
        {
          'core-table__td--snugfit':
            snugfit || variant === TableCellVariantNS.snugfit,
        },
        className
      )}
      {...props}
    >
      {children}
    </td>
  )
)

export const CurrencyCell = React.forwardRef<
  HTMLDivElement,
  DivAttributes & CurrencyCellProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    className={cx(
      'core-table__text-cell',
      'core-table__text-cell--currency',
      className
    )}
  >
    {children}
  </div>
))

export const IconCell = React.forwardRef<
  HTMLDivElement,
  DivAttributes & IconCellProps
>(({ className, children, ...props }, ref) => (
  <div ref={ref} {...props} className={cx('core-table__icon-cell', className)}>
    {children}
  </div>
))

export const InputCell = React.forwardRef<
  HTMLInputElement,
  InputAttributes & InputCellProps
>(
  (
    {
      className,
      errorMessage = '',
      loading = false,
      size = SizeNS.block,
      ...props
    },
    ref
  ) => (
    <div
      className={cx(
        'core-table__inline-edit__container',
        `core-table__inline-edit__container--${size}`,
        {
          ['core-table__inline-edit__container--error']: errorMessage,
        }
      )}
    >
      {loading && (
        <div className={cx('core-table__loading__container')}>
          <Spinner loading={loading} size={SizeNS.md} />
        </div>
      )}
      <input
        {...props}
        ref={ref}
        className={cx('core-table__input-cell', className)}
      />
      {!loading && errorMessage && (
        <div
          className={cx(
            `core-table__inline-edit__error-box`,
            `core-table__inline-edit__error-box--${size}`
          )}
        >
          {errorMessage}
        </div>
      )}
    </div>
  )
)

export const LinkCell = React.forwardRef<
  HTMLDivElement,
  AnchorAttributes & LinkCellProps
>(({ className, children, href = '', ...props }, ref) => (
  <TextCell ref={ref}>
    <LinkElement className={className} href={href} {...props}>
      {children}
    </LinkElement>
  </TextCell>
))

export const PercentCell = React.forwardRef<
  HTMLDivElement,
  DivAttributes & PercentCellProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    className={cx(
      'core-table__text-cell',
      'core-table__text-cell--percent',
      className
    )}
  >
    {children}
  </div>
))

export const SelectCell = React.forwardRef<HTMLDivElement, SelectCellProps>(
  (
    { children, className, errorMessage, processing = false, ...props },
    ref
  ) => (
    <div
      ref={ref}
      className={cx(
        'core-table__inline-edit__container',
        `core-table__inline-edit__container--block`,
        {
          ['core-table__inline-edit__container--error']: errorMessage,
        }
      )}
    >
      {processing && (
        <div className={cx('core-table__loading__container')}>
          <Spinner loading={processing} size={SizeNS.md} />
        </div>
      )}
      <Select
        {...props}
        block
        className={cx(
          selectStyles['core-select--table'],
          'core-table__select-cell',
          className
        )}
        error={!!errorMessage}
      >
        {children}
      </Select>
      {!processing && errorMessage && (
        <div className={cx(`core-table__inline-edit__error-box`)}>
          {errorMessage}
        </div>
      )}
    </div>
  )
)

export const TextAreaCell = React.forwardRef<
  HTMLTextAreaElement,
  TextAreaAttributes & TextAreaCellProps
>(
  (
    {
      className,
      errorMessage = '',
      loading = false,
      resizable = false,
      ...props
    },
    ref
  ) => (
    <div
      className={cx(
        'core-table__inline-edit__container',
        `core-table__inline-edit__container--block`,
        {
          ['core-table__inline-edit__container--error']: errorMessage,
        }
      )}
    >
      {loading && (
        <div className={cx('core-table__loading__container')}>
          <Spinner loading={loading} size={SizeNS.md} />
        </div>
      )}
      <BaseTextArea
        {...props}
        ref={ref}
        className={cx('core-table__textarea-cell', className)}
        resize={resizable ? TextAreaResizeNS.vertical : TextAreaResizeNS.none}
      />
      {!loading && errorMessage && (
        <div className={cx(`core-table__inline-edit__error-box`)}>
          {errorMessage}
        </div>
      )}
    </div>
  )
)

export const TextCell = React.forwardRef<
  HTMLDivElement,
  DivAttributes & TextCellProps
>(({ className, children, ...props }, ref) => (
  <div ref={ref} {...props} className={cx('core-table__text-cell', className)}>
    {children}
  </div>
))

export const TableCells = {
  Currency: withDeprecation({
    oldThing: 'TableCells.Currency',
    newThing: 'Table.CurrencyCell',
    link: 'https://procore.github.io/core/latest/#/react/components/tables',
  })(CurrencyCell),

  Icon: withDeprecation({
    oldThing: 'TableCells.Icon',
    newThing: 'Table.IconCell',
    link: 'https://procore.github.io/core/latest/#/react/components/tables',
  })(IconCell),

  Input: withDeprecation({
    oldThing: 'TableCells.Input',
    newThing: 'Table.InputCell',
    link: 'https://procore.github.io/core/latest/#/react/components/tables',
  })(InputCell),

  Link: withDeprecation({
    oldThing: 'TableCells.Link',
    newThing: 'Table.LinkCell',
    link: 'https://procore.github.io/core/latest/#/react/components/tables',
  }),

  Percent: withDeprecation({
    oldThing: 'TableCells.Percent',
    newThing: 'Table.PercentCell',
    link: 'https://procore.github.io/core/latest/#/react/components/tables',
  })(PercentCell),

  Text: withDeprecation({
    oldThing: 'TableCells.Text',
    newThing: 'Table.TextCell',
    link: 'https://procore.github.io/core/latest/#/react/components/tables',
  })(TextCell),

  TextArea: withDeprecation({
    oldThing: 'TableCells.TextArea',
    newThing: 'Table.TextAreaCell',
    link: 'https://procore.github.io/core/latest/#/react/components/tables',
  })(TextAreaCell),
}

Table.displayName = 'Table'

Body.displayName = 'Table.Body'

BodyCell.displayName = 'Table.BodyCell'

BodyRow.displayName = 'Table.BodyRow'

Carat.displayName = 'Table.Carat'

Checkbox.displayName = 'Table.Checkbox'

Container.displayName = 'Table.Container'

CurrencyCell.displayName = 'Table.CurrencyCell'

Group.displayName = 'Table.Group'

GroupTitle.displayName = 'Table.GroupTitle'

Header.displayName = 'Table.Header'

HeaderCell.displayName = 'Table.HeaderCell'

HeaderRow.displayName = 'Table.HeaderRow'

IconCell.displayName = 'Table.IconCell'

InputCell.displayName = 'Table.InputCell'

LinkCell.displayName = 'Table.LinkCell'

PercentCell.displayName = 'Table.ParentCell'

TextAreaCell.displayName = 'Table.TextAreaCell'

TextCell.displayName = 'Table.TextCell'

export default addSubcomponents(
  {
    Body,
    BodyCell,
    BodyRow,
    Carat,
    Checkbox,
    Container,
    CurrencyCell,
    Group,
    GroupTitle,
    Header,
    HeaderCell,
    HeaderRow,
    IconCell,
    InputCell,
    LinkCell,
    PercentCell,
    SelectCell,
    TextAreaCell,
    TextCell,
  },
  Table
)
